import { useMutation, useQuery } from '@tanstack/react-query'
import { useAuth0 } from '@/utils/auth/auth0'
import * as API from '@/api/databaseSearchAPI'
import type { DatabaseSearchRequest } from '@/models/DatabaseSearch'

export function useSearchTags(orgUuid: string | undefined) {
  const { getAccessTokenSilently } = useAuth0()

  const query = useQuery({
    queryKey: ['databaseSearchTags', orgUuid],
    queryFn: async () => {
      const freshToken = await getAccessTokenSilently()
      return API.fetchSearchTags(freshToken, orgUuid as string)
    },
    enabled: !!orgUuid,
    staleTime: Infinity,
  })

  return {
    tags: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
  }
}

/**
 * Runs a search only when asked. Every request counts toward the user's rate
 * limit, so there are no retries and no refetching.
 */
export function useDatabaseSearch(orgUuid: string | undefined) {
  const { getAccessTokenSilently } = useAuth0()

  return useMutation({
    mutationFn: async (request: DatabaseSearchRequest) => {
      const freshToken = await getAccessTokenSilently()
      return API.searchDatabase(freshToken, orgUuid as string, request)
    },
    retry: false,
  })
}
